import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_MENU_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { fn, hash } from '@ember/helper';
import Menu from './filterable.gjs';
import MenuItem from '../core/menu/item.gjs';
import NoResults from '../core/menu/no-results.gjs';
import Super from '../core/menu/parent.gjs';
import boldMatchingText from '../../../modifiers/bold-matching-text';

const Item = <template>
  <MenuItem
    @onClick={{@onClick}}
    @registerComponent={{@registerComponent}}
    @retainSelection={{@retainSelection}}
    @unregisterComponent={{@unregisterComponent}}
    @value={{@value}}
    class={{@inputClasses}}
    {{boldMatchingText @searchValue}}
    ...attributes
  >
    {{yield}}
  </MenuItem>
</template>;

const METADATA = Metadata.define({
  description: `
Unified single-select menu shared by \`Spds::Input::Dropdown::List\` and \`Spds::Input::Search\`. It composes
\`Spds::Menu::Filterable\` and registers each yielded \`item\` so the menu can power keyboard navigation and
single-select state.

### Search modes
- When no \`@searchValue\` is supplied (dropdown usage), the menu's built-in filterable search is used
  (\`@searchable\`/\`@searchThreshold\`/\`@searchPlaceholder\`).
- When \`@searchValue\` is supplied (search-input usage), the parent owns filtering; the built-in search is
  disabled and matches are highlighted via the yielded \`item\`.`,
  extends: [
    pick(MENU_ARGS, ['anchorElement']),
    pick(ACTION_ARGS, [{ name: 'onSelect', required: true }]),
    pick(COMPONENT_STATE_ARGS, [{ name: 'open', required: true }]),
    pick(INPUT_MENU_ARGS, [
      'displayedItemCount',
      'noResultsText',
      'retainSelection',
      'searchable',
      'searchPlaceholder',
      'searchThreshold',
    ]),
  ],
  meta: {
    autoFilter: Arg.boolean({
      default: true,
      description:
        'When true, the filterable menu applies its built-in text filter. Set false so a parent owns filtering (custom/async search); the yielded item then handles match highlighting.',
    }),
    searchValue: Arg.string({
      description: 'External filter value; enables match highlighting and disables built-in search',
    }),
  },
  name: 'Select Menu',
});

export default class SpdsMenuSelect extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-select';

  get autoFilter() {
    return this.argOrDefault('autoFilter');
  }

  get itemClass() {
    return this.useInternalSearch ? 'spds-input-dropdown-list-item' : 'spds-input-search-menu-item';
  }

  get menuClass() {
    return this.useInternalSearch ? 'spds-input-dropdown-list-menu' : 'spds-input-search-menu';
  }

  get noItemsDisplayed() {
    let { displayedItemCount } = this.args;

    return typeof displayedItemCount === 'number' ? displayedItemCount === 0 : undefined;
  }

  get searchable() {
    return this.useInternalSearch ? this.args.searchable : false;
  }

  get useInternalSearch() {
    return this.args.searchValue === undefined;
  }

  <template>
    <Menu
      @anchorElement={{@anchorElement}}
      @animated={{true}}
      @autoFilter={{this.autoFilter}}
      @id={{this.id}}
      @items={{this.navigableOptions}}
      @noItemsDisplayed={{this.noItemsDisplayed}}
      @noResultsText={{@noResultsText}}
      @open={{@open}}
      @searchable={{this.searchable}}
      @searchPlaceholder={{@searchPlaceholder}}
      @searchThreshold={{@searchThreshold}}
      @searchValue={{@searchValue}}
      class="{{this.classes}} {{this.menuClass}}"
      data-test-spds-input-dropdown-list-menu
      data-test-spds-input-search-menu
      data-test-spds-menu-select
      {{this.validateArguments}}
      ...attributes
    >
      <:body>
        {{yield
          (hash
            item=(component
              Item
              inputClasses=this.itemClass
              onClick=@onSelect
              registerComponent=(fn this.registerItem false)
              retainSelection=@retainSelection
              searchValue=(if this.autoFilter undefined @searchValue)
              unregisterComponent=this.unregisterItem
            )
            noResults=(component NoResults)
          )
        }}
      </:body>
    </Menu>
  </template>
}

export const SPDS_MENU_SELECT_METADATA = SpdsMenuSelect.metadata;
